import { numFont } from '../../utils/fonts';
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Paper,
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  StepConnector,
  Chip,
  Alert,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import DraftsOutlinedIcon from '@mui/icons-material/DraftsOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

const STEP_CONFIG = {
  sent: {
    icon: SendOutlinedIcon,
    labelKey: 'delivery.timeline.sent',
    color: 'success',
  },
  delivered: {
    icon: MarkEmailReadOutlinedIcon,
    labelKey: 'delivery.timeline.delivered',
    color: 'success',
  },
  email_opened: {
    icon: DraftsOutlinedIcon,
    labelKey: 'delivery.timeline.email_opened',
    color: 'success',
  },
  invoice_opened: {
    icon: DescriptionOutlinedIcon,
    labelKey: 'delivery.timeline.invoice_opened',
    color: 'success',
  },
  paid: {
    icon: CheckCircleOutlinedIcon,
    labelKey: 'delivery.timeline.paid',
    color: 'success',
  },
  bounced: {
    icon: ErrorOutlineIcon,
    labelKey: 'delivery.timeline.bounced',
    color: 'error',
  },
};

const DELIVERY_METHOD_LABEL_KEYS = {
  email: 'delivery.method.email',
  verkkolasku: 'delivery.method.verkkolasku',
  email_maventa: 'delivery.method.email_maventa',
  paper_priority: 'delivery.method.paper_priority',
  paper_economy: 'delivery.method.paper_economy',
  pdf: 'delivery.method.pdf',
  manual: 'delivery.method.manual',
};

const TimelineConnector = styled(StepConnector)(({ theme }) => ({
  '&.MuiStepConnector-root': {
    top: 20,
  },
  '& .MuiStepConnector-line': {
    borderTopWidth: 2,
    borderColor: theme.palette.grey[300],
  },
  '&.Mui-active .MuiStepConnector-line, &.Mui-completed .MuiStepConnector-line': {
    borderColor: theme.palette.success.main,
  },
}));

function StepIcon({ step, completed }) {
  const config = STEP_CONFIG[step];
  if (!config) return null;
  const Icon = config.icon;

  if (step === 'bounced') {
    return (
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'error.light',
          color: 'error.contrastText',
        }}
      >
        <Icon fontSize="small" />
      </Box>
    );
  }

  if (completed) {
    return (
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'success.main',
          color: 'success.contrastText',
        }}
      >
        <Icon fontSize="small" />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'grey.200',
        color: 'grey.500',
      }}
    >
      <Icon fontSize="small" />
    </Box>
  );
}

function formatTimestamp(isoString) {
  if (!isoString) return null;
  try {
    // Date-only string (e.g. "2026-05-26") — show without time
    if (isoString.length === 10 && !isoString.includes('T')) {
      const date = new Date(isoString + 'T00:00:00');
      return date.toLocaleDateString('fi-FI', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    }
    const date = new Date(isoString);
    return date.toLocaleString('fi-FI', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return null;
  }
}

export default function InvoiceDeliveryTimeline({ timeline, deliveryMethod, maventaId, errorMessage }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (!timeline || !timeline.steps || timeline.steps.length === 0) {
    return null;
  }

  const { steps, has_error, error_detail } = timeline;
  const methodLabelKey = DELIVERY_METHOD_LABEL_KEYS[deliveryMethod];
  const methodLabel = methodLabelKey ? t(methodLabelKey) : deliveryMethod;

  // Find the active step index (first non-completed step, or last step if all completed)
  const activeStepIndex = steps.findIndex((s) => !s.completed);
  const activeStep = activeStepIndex === -1 ? steps.length : activeStepIndex;

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <SendOutlinedIcon color="primary" />
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'primary.main' }}>
          {t('delivery.timeline.title', 'Laskun toimitustilanne')}
        </Typography>
        <Chip label={methodLabel} size="small" variant="outlined" />
      </Box>

      {maventaId && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Maventa ID:{' '}
            <Typography component="span" variant="caption" sx={{ ...numFont, fontSize: '11px' }}>
              {maventaId}
            </Typography>
          </Typography>
        </Box>
      )}

      <Stepper
        activeStep={activeStep}
        alternativeLabel={!isMobile}
        orientation={isMobile ? 'vertical' : 'horizontal'}
        connector={<TimelineConnector />}
        sx={{ py: 1 }}
      >
        {steps.map((step) => {
          const config = STEP_CONFIG[step.step];
          if (!config) return null;
          const timestamp = formatTimestamp(step.occurred_at);

          return (
            <Step key={step.step} completed={step.completed}>
              <StepLabel
                StepIconComponent={() => <StepIcon step={step.step} completed={step.completed} />}
                optional={
                  timestamp && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center' }}>
                      {timestamp}
                    </Typography>
                  )
                }
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: step.completed ? 600 : 400,
                    color: step.step === 'bounced' ? 'error.main' : step.completed ? 'text.primary' : 'text.disabled',
                  }}
                >
                  {t(config.labelKey)}
                </Typography>
              </StepLabel>
            </Step>
          );
        })}
      </Stepper>

      {has_error && (error_detail || errorMessage) && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error_detail || errorMessage}
        </Alert>
      )}
    </Paper>
  );
}
